// Real volunteer sign-ups, backed by our self-hosted PostgREST + Postgres (RLS).
// A signup writes a row the member owns; the DB awards +50 Ihsan Points (append-only
// ledger) and keeps an aggregate count per opportunity. Auth token comes from GoTrue
// via session.js. Baked default URL mirrors session.js/directus.js so a plain build works.
import { getAccessToken, getMemberIdentity, SUPABASE_ANON } from './session';

const PGREST_URL = (process.env.REACT_APP_POSTGREST_URL
  || 'https://yuftrqcahapyrblxvkns.supabase.co/rest/v1').replace(/\/$/, '');
// The department's Volunteer Management System (hosted in our GCP) is the source
// of truth for the application lifecycle. A Join here creates a real VMS
// application (Pending -> coordinator Accepts); we mirror it locally for points
// + fast display and cache its status.
const VMS_URL = (process.env.REACT_APP_VMS_URL
  || 'https://vms-jgmy2ghs6a-as.a.run.app').replace(/\/$/, '');

async function authHeaders(extra = {}) {
  const token = await getAccessToken();
  if (!token) return null;
  return { apikey: SUPABASE_ANON, authorization: `Bearer ${token}`, ...extra };
}

// Create (or find) the real VMS application for this member + opportunity.
// Returns { applicationId, status } or null if it couldn't be created (e.g. the
// member has no mobile on file, or VMS is unreachable) — the local signup still
// records so points/My-Engagements keep working.
async function applyToVms(listingId) {
  const id = getMemberIdentity();
  if (!id || !id.email || !id.mobile) return null; // VMS requires contact details
  try {
    const res = await fetch(`${VMS_URL}/api/public/apply/${encodeURIComponent(String(listingId))}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        fullName: id.fullName, email: id.email, mobile: id.mobile,
        source: 'PartnerOrg', formResponses: { channel: 'IhsanOne' },
      }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok || res.status === 409) return { applicationId: d.applicationId || null, status: d.status || 'Pending' };
    return null;
  } catch {
    return null;
  }
}

// Live lifecycle status of a VMS application (Pending/Shortlisted/Accepted/...).
export async function refreshVmsStatus(applicationId) {
  if (!applicationId) return null;
  try {
    const res = await fetch(`${VMS_URL}/api/public/application-status/${encodeURIComponent(applicationId)}`);
    if (!res.ok) return null;
    return (await res.json()).status || null;
  } catch {
    return null;
  }
}

// Sign up. Returns { ok, already?, error? }. A duplicate (already signed up) is
// treated as success so the UI lands in the joined state either way.
export async function signUpForOpportunity(listingId, listingTitle) {
  const headers = await authHeaders({ 'content-type': 'application/json', Prefer: 'return=minimal' });
  if (!headers) return { ok: false, error: 'Please sign in first.' };
  // 1) Create the real VMS application (source of truth for the lifecycle).
  const vms = await applyToVms(listingId);
  // 2) Mirror locally (awards points, powers My Engagements + live count).
  //    Only listing_id/listing_title/vms_application_id are client-writable;
  //    user_id/status/vms_status/created_at are server-set (anti-spoofing).
  //    Retry a few times to ride out a PostgREST cold-start 503.
  const body = JSON.stringify({
    listing_id: String(listingId),
    listing_title: listingTitle || null,
    vms_application_id: vms ? vms.applicationId : null,
  });
  let lastErr = '';
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await fetch(`${PGREST_URL}/volunteer_signups`, { method: 'POST', headers, body });
      if (res.status === 409) return { ok: true, already: true };
      if (res.ok) return { ok: true };
      lastErr = (await res.text().catch(() => '')) || `HTTP ${res.status}`;
      if (res.status !== 503 && res.status !== 502 && res.status !== 504) break; // only retry transient
    } catch (e) {
      lastErr = String(e);
    }
    await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
  }
  return { ok: false, error: lastErr || 'Could not record your sign-up.' };
}

export async function cancelSignup(listingId) {
  const headers = await authHeaders();
  if (!headers) return { ok: false };
  try {
    const res = await fetch(
      `${PGREST_URL}/volunteer_signups?listing_id=eq.${encodeURIComponent(String(listingId))}`,
      { method: 'DELETE', headers },
    );
    return { ok: res.ok };
  } catch {
    return { ok: false };
  }
}

// The current member's volunteer sign-ups, newest first.
export async function getMySignups() {
  const headers = await authHeaders();
  if (!headers) return [];
  try {
    const res = await fetch(
      `${PGREST_URL}/volunteer_signups?select=listing_id,listing_title,status,created_at,vms_application_id,vms_status&order=created_at.desc`,
      { headers },
    );
    if (!res.ok) return [];
    return (await res.json()) || [];
  } catch {
    return [];
  }
}

export async function hasSignedUp(listingId) {
  const headers = await authHeaders();
  if (!headers) return false;
  try {
    const res = await fetch(
      `${PGREST_URL}/volunteer_signups?select=listing_id&listing_id=eq.${encodeURIComponent(String(listingId))}`,
      { headers },
    );
    if (!res.ok) return false;
    return ((await res.json()) || []).length > 0;
  } catch {
    return false;
  }
}

// Aggregate signup count for one opportunity. Public (no token needed) — the counts
// table exposes no user data. Returns a number, or null if unavailable.
export async function getSignupCount(listingId) {
  try {
    const res = await fetch(
      `${PGREST_URL}/volunteer_counts?select=signups&listing_id=eq.${encodeURIComponent(String(listingId))}`,
      { headers: { apikey: SUPABASE_ANON } },
    );
    if (!res.ok) return null;
    const rows = (await res.json()) || [];
    return rows.length ? rows[0].signups : 0;
  } catch {
    return null;
  }
}
